import { useState, useEffect } from 'react';
import { Check, CreditCard, Zap, Users, Shield, TrendingUp } from 'lucide-react';

interface PlanOption {
  id: string;
  name: string;
  price: number;
  displayPrice: string;
  features: string[];
  optimizations: number;
  popular?: boolean;
}

const plans: PlanOption[] = [
  {
    id: 'basic',
    name: 'Basic Plan',
    price: 297,
    displayPrice: '$297',
    optimizations: 100,
    features: [
      '100 monthly resume optimizations',
      'AI-powered optimization engine',
      'ATS compatibility scoring',
      'All file formats (TXT, PDF, DOCX)',
      'WordPress plugin installation',
      'Email support',
      'Regular updates included'
    ]
  },
  {
    id: 'professional',
    name: 'Professional Plan',
    price: 597,
    displayPrice: '$597',
    optimizations: 500,
    popular: true,
    features: [
      '500 monthly resume optimizations',
      'AI-powered optimization engine',
      'ATS compatibility scoring',
      'All file formats (TXT, PDF, DOCX)',
      'WordPress plugin installation',
      'Priority email support',
      'Regular updates included',
      'Advanced analytics dashboard',
      'Custom branding options'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise Plan',
    price: 997,
    displayPrice: '$997',
    optimizations: 999999,
    features: [
      'Unlimited monthly resume optimizations',
      'AI-powered optimization engine',
      'ATS compatibility scoring',
      'All file formats (TXT, PDF, DOCX)',
      'WordPress plugin installation',
      'Priority support with dedicated success manager',
      'Regular updates included',
      'Advanced analytics dashboard',
      'Full white-label customization',
      'API access for custom integrations'
    ]
  }
];

export function SiteOwnerPayment() {
  const [selectedPlan, setSelectedPlan] = useState<string>('professional');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState<Record<string, string>>({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setTrackingData({
      source: urlParams.get('source') || 'landing_page',
      ctaLocation: urlParams.get('cta_location') || '',
      utmSource: urlParams.get('utm_source') || '',
      utmMedium: urlParams.get('utm_medium') || '',
      utmCampaign: urlParams.get('utm_campaign') || '',
    });
  }, []);

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !fullName) {
      setError('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (fullName.trim().length < 2) {
      setError('Please enter your full name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-owner-checkout`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planType: selectedPlan,
            email: email.trim().toLowerCase(),
            fullName: fullName.trim(),
            ctaLocation: trackingData.ctaLocation,
            source: trackingData.source,
            utmSource: trackingData.utmSource,
            utmMedium: trackingData.utmMedium,
            utmCampaign: trackingData.utmCampaign,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to create checkout session';
        const hint = errorData.hint ? `\n\n${errorData.hint}` : '';
        throw new Error(errorMessage + hint);
      }

      const data = await response.json();

      if (!data.url || typeof data.url !== 'string') {
        console.error('Invalid checkout response:', data);
        throw new Error('Invalid checkout session. Please try again or contact support.');
      }

      try {
        new URL(data.url);
      } catch {
        console.error('Invalid URL format:', data.url);
        throw new Error('Invalid checkout URL. Please try again or contact support.');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const selectedPlanDetails = plans.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your License Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select the plan that fits your needs and start monetizing your WordPress site today
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 ${
                selectedPlan === plan.id
                  ? 'border-2 border-blue-500 shadow-xl scale-105'
                  : 'border border-gray-200 shadow-md hover:shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-5xl font-extrabold text-gray-900">{plan.displayPrice}</span>
                  <span className="text-gray-600 text-lg">/year</span>
                </div>
                <div className="text-blue-600 font-semibold text-lg">
                  {plan.optimizations === 999999 ? 'Unlimited' : plan.optimizations} optimizations/month
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-center">
                <input
                  type="radio"
                  name="plan"
                  checked={selectedPlan === plan.id}
                  onChange={() => setSelectedPlan(plan.id)}
                  className="w-5 h-5 text-blue-600"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Complete Your Purchase</h2>

          <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Selected Plan</h3>
              <span className="text-3xl font-bold text-blue-600">
                {selectedPlanDetails?.displayPrice}
              </span>
            </div>
            <p className="text-gray-700 font-semibold mb-2">{selectedPlanDetails?.name}</p>
            <p className="text-gray-600">
              {selectedPlanDetails?.optimizations === 999999
                ? 'Unlimited'
                : selectedPlanDetails?.optimizations} monthly resume optimizations
            </p>
          </div>

          <form onSubmit={handleCheckout} className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-bold text-gray-900 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="john@example.com"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                <div className="whitespace-pre-wrap">{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {loading ? (
                <>Processing...</>
              ) : (
                <>
                  <CreditCard className="w-6 h-6" />
                  Proceed to Secure Checkout
                </>
              )}
            </button>

            <p className="text-center text-gray-600 text-sm">
              You will be redirected to Stripe for secure payment processing
            </p>
          </form>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
            <Shield className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <h4 className="font-bold text-gray-900 mb-1">Secure Payment</h4>
            <p className="text-sm text-gray-600">Powered by Stripe</p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
            <Zap className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h4 className="font-bold text-gray-900 mb-1">Instant Access</h4>
            <p className="text-sm text-gray-600">Immediate license delivery</p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
            <Users className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <h4 className="font-bold text-gray-900 mb-1">500+ Customers</h4>
            <p className="text-sm text-gray-600">Trusted by site owners</p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
            <TrendingUp className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h4 className="font-bold text-gray-900 mb-1">Start Earning</h4>
            <p className="text-sm text-gray-600">Launch in 15 minutes</p>
          </div>
        </div>
      </div>
    </div>
  );
}
